import {Injectable} from "@angular/core";
import {firstValueFrom, fromEvent, merge, Observable} from "rxjs";
import {Mimeinfo, mimetyperSomFilInputAccept, tillatteMimetyper} from "felles-ng2/støttefunksjoner/mimetyper";
import {map} from "rxjs/operators";
import {fileListSomArray} from "felles-ng2/støttefunksjoner/dom";
import {erStreng} from "felles-ng2/støttefunksjoner/strenger";
import {logVerdiLevert} from "felles-ng2/støttefunksjoner/observable";
import {første} from "felles-ng2/støttefunksjoner/lister";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";

/** Presenterer fil-velger dialog, returnerer valgte filer. */
@Injectable({providedIn: 'root'})
export class FilvelgerViaInputFelt {

    protected inputfelt: HTMLInputElement;
    private inputHendelser: Observable<File[]>;

    constructor() {
        this.inputfelt = this.opprettInputFelt();
        this.inputHendelser = merge(fromEvent(this.inputfelt, 'change'), fromEvent(this.inputfelt, 'cancel'))
          .pipe(
            logVerdiLevert('presenterFilvelger'),
            map(hendelse => hendelse.type == 'change' ? fileListSomArray(this.inputfelt.files!) : []));
    }

    /** Presenterer fil-velger dialog, returnerer valgte filer, tom liste dersom ingen filer valgt. */
    async presenterFilvelger(konfigurasjon: DialogKonfigurasjon): Promise<File[]> {
        this.presenter(konfigurasjon);
        return this.valgtFilSomPromise();
    }

    /** Presenterer fil-velger dialog, lar bruker velge multiple fil, returnerer tom liste dersom ingen fil valgt.
     * Kun filer ihht {@link tillatteMimetyper} kan velges. */
    async presenterFilvelgerMultipleFilerStandardformat(): Promise<File[]> {
        return this.presenterFilvelger({tillateMimetyper: tillatteMimetyper(), tillatMultiple: true});
    }

    /** Presenterer fil-velger dialog, lar bruker velge én fil, returnerer null dersom ingen fil valgt.
     * Kun filer ihht {@link tillatteMimetyper} kan velges. */
    async presenterFilvelgerEnkeltfilStandardformat(): Promise<File | null> {
        const valgt = await this.presenterFilvelger({tillateMimetyper: tillatteMimetyper(), tillatMultiple: false});
        return første(valgt);
    }

    //private hjelpemetoder

    private opprettInputFelt() {
        //sørg for å fjerne eventuelt eksisterende element, noe som kan skje feks ved kjøring i storybook som ikke relaster hele siden men kun re-instansierer angular-applkasjonen
        const eksisterendeFelt = document.getElementById('filvelger-via-inputfelt') as HTMLInputElement | null;
        if (erIkkeNil(eksisterendeFelt) && eksisterendeFelt.type == 'file') eksisterendeFelt.remove()

        const inputfelt = document.createElement('input');
        inputfelt.type = 'file';
        inputfelt.style.display = 'none';
        inputfelt.setAttribute('id', 'filvelger-via-inputfelt');
        inputfelt.setAttribute('data-testid', 'filvelger-via-inputfelt');
        document.body.appendChild(inputfelt);
        return inputfelt;
    }

    private presenter(konfigurasjon: DialogKonfigurasjon) {
        this.inputfelt.value = ''; //viktig - ellers leveres ingen verdi om samme fil velges to ganger på rad..
        this.inputfelt.accept = erStreng(konfigurasjon.tillateMimetyper) ?
          konfigurasjon.tillateMimetyper :
          mimetyperSomFilInputAccept(konfigurasjon.tillateMimetyper);
        this.inputfelt.multiple = konfigurasjon.tillatMultiple ?? false;
        this.inputfelt.click();
    }

    protected valgtFilSomPromise(): Promise<File[]> {
        return firstValueFrom(this.inputHendelser, {defaultValue: []});
    }
}

export interface DialogKonfigurasjon {
    /** Hvilke filtyper som skal være tilgjengelig for valg i fil-velger dialogen */
    tillateMimetyper: Mimeinfo[] | string;
    /** Hvorvidt bruker skal kunne velge multiple filer */
    tillatMultiple?: boolean;
}
